// frontend/src/pages/DeviceDetail.js
import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useFarm } from '../context/FarmContext';
import { useDeviceProvisioning } from '../hooks/useDeviceProvisioning';
import PlotHeader from '../components/plot/PlotHeader';
import DeviceList from '../components/plot/DeviceList';
import DisplayControls from '../components/plot/DisplayControls';
import { MetricCategoryPanel } from '../components/plot/MetricCharts';

const DEVICE_COLORS = ['#2563eb', '#16a34a', '#d97706', '#9333ea', '#dc2626', '#0891b2'];

const METRIC_META_MAP = {
  battery: { label: 'Battery Voltage', unit: 'V' },
  wifi: { label: 'WiFi Signal Strength', unit: 'dBm' },
  disk: { label: 'Disk Free', unit: '%' },
  distance: { label: 'Distance / Water Level / Snow Depth', unit: 'mm' },
  temperature: { label: 'Air Temperature', unit: '°C' },
  humidity: { label: 'Relative Humidity', unit: '%' },
  dewpoint: { label: 'Dew Point', unit: '°C' },
  pressure: { label: 'Barometric Pressure', unit: 'hPa' },
  soil_m: { label: 'Soil Moisture', unit: '%' },
  leafw: { label: 'Leaf Wetness', unit: '%' },
  wind_speed: { label: 'Wind Speed', unit: 'km/h' }
};

const STATE_KEYS = new Set(['battery', 'wifi', 'disk']);

const CATEGORY_TITLES = {
  deviceState: 'Device State & Diagnostics',
  sensorData: 'Raw Sensor Observations'
};

export default function DeviceDetail() {
  const { farmId, plotId } = useParams();
  const location = useLocation();
  const { farmSummary, devices: allFarmDevices, loading: farmLoading } = useFarm();

  const initialDeviceId = location.state?.selectedDeviceId || null;

  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [activeMetrics, setActiveMetrics] = useState([]);
  const [readings, setReadings] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);
  const [forecastVisible, setForecastVisible] = useState(false);
  const [localTimeEnabled, setLocalTimeEnabled] = useState(true);
  const [disabledSections, setDisabledSections] = useState({});

  // Device Provisioning hook
  const { openAddDeviceModal, DeviceProvisioningModal } = useDeviceProvisioning();

  // 1. Fetch physical device telemetry observations
  useEffect(() => {
    let isMounted = true;
    async function fetchReadings() {
      if (!plotId) return;
      setDataLoading(true);
      setDataError(null);
      try {
        const datetime = new Date().toISOString().replace('T', ' ');
        const res = await axios.get('/api/devices/readings', {
          params: { plotId, datetime },
          withCredentials: true
        });
        const data = res.data;

        if (isMounted) {
          setReadings(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (isMounted) {
          console.error(`Failed to load device telemetry for plot ${plotId}:`, err);
          setDataError(err.message || 'Failed to load telemetry readings');
        }
      } finally {
        if (isMounted) setDataLoading(false);
      }
    }

    fetchReadings();
    return () => { isMounted = false; };
  }, [plotId]);

  const currentPlot = useMemo(() => {
    return farmSummary?.plots?.find(
      (p) => String(p.id || p.site_id || p.plotId) === String(plotId)
    );
  }, [farmSummary, plotId]);

  // 2. Filter strictly for PHYSICAL devices (excluding proxy/virtual feeds)
  const physicalDevices = useMemo(() => {
    return (allFarmDevices || [])
      .filter((d) => d.device_type !== 'proxy' && !d.is_proxy && !d.isProxy)
      .map((dev, idx) => ({
        ...dev,
        id: dev.id || dev.device_id,
        name: dev.name || `Sensor ${(dev.id || dev.device_id || '').slice(0, 6)}`,
        location: dev.location || 'In-field Sensor',
        color: dev.color || DEVICE_COLORS[idx % DEVICE_COLORS.length]
      }));
  }, [allFarmDevices]);

  // 3. Default device selection
  useEffect(() => {
    if (physicalDevices.length > 0 && selectedDeviceIds.length === 0) {
      if (initialDeviceId && physicalDevices.some((d) => String(d.id) === String(initialDeviceId))) {
        setSelectedDeviceIds([initialDeviceId]);
      } else {
        setSelectedDeviceIds([physicalDevices[0].id]);
      }
    }
  }, [physicalDevices, initialDeviceId, selectedDeviceIds]);

  // 4. Transform observations into Recharts-compatible chartData
  const { chartData, categoryGroups } = useMemo(() => {
    if (!readings || readings.length === 0) {
      return { chartData: [], categoryGroups: { deviceState: [], sensorData: [] } };
    }

    const timeMap = {};
    const stateKeysFound = new Set();
    const sensorKeysFound = new Set();

    readings.forEach((item) => {
      const { device_id, time, key, value } = item;
      if (!time || !key || value === undefined) return;

      const displayTime = time.includes(' ') ? time.split(' ')[1] : time;

      if (!timeMap[time]) {
        timeMap[time] = { time: displayTime, rawTime: time, timestamp: new Date(time).getTime() };
      }

      timeMap[time][`${key}_${device_id}_hist`] = Number(value);

      if (STATE_KEYS.has(key)) {
        stateKeysFound.add(key);
      } else {
        sensorKeysFound.add(key);
      }
    });

    const sortedChartData = Object.values(timeMap).sort((a, b) => new Date(a.rawTime) - new Date(b.rawTime));

    const buildMetricObj = (k) => ({
      type: k,
      label: METRIC_META_MAP[k]?.label || (k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, ' ')),
      unit: METRIC_META_MAP[k]?.unit || ''
    });

    return {
      chartData: sortedChartData,
      categoryGroups: {
        deviceState: Array.from(stateKeysFound).map(buildMetricObj),
        sensorData: Array.from(sensorKeysFound).map(buildMetricObj)
      }
    };
  }, [readings]);

  // 5. Default active metrics
  useEffect(() => {
    const allAvailable = [
      ...categoryGroups.deviceState.map((m) => m.type),
      ...categoryGroups.sensorData.map((m) => m.type)
    ];
    if (allAvailable.length > 0 && activeMetrics.length === 0) {
      setActiveMetrics(allAvailable);
    }
  }, [categoryGroups, activeMetrics]);

  const visibleDevices = useMemo(() => {
    return physicalDevices.filter((d) => selectedDeviceIds.includes(d.id));
  }, [physicalDevices, selectedDeviceIds]);

  const handleToggleDevice = (deviceId) => {
    setSelectedDeviceIds((prev) =>
      prev.includes(deviceId)
        ? prev.filter((id) => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const handleToggleMetric = (metricType) => {
    setActiveMetrics((prev) =>
      prev.includes(metricType)
        ? prev.filter((m) => m !== metricType)
        : [...prev, metricType]
    );
  };

  if (farmLoading || dataLoading) {
    return <div style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>Loading device telemetry...</div>;
  }

  if (dataError) {
    return <div style={{ padding: '32px', color: '#dc2626' }}>Error: {dataError}</div>;
  }

  return (
    <div className="device-detail-page" style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh', fontFamily: 'sans-serif' }}>

      {/* Header & Hardware Devices List */}
      <div style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
        <PlotHeader metadata={currentPlot || { name: `Plot ${plotId}` }} plotId={plotId} farmId={farmId} />
        <DeviceList
          devices={physicalDevices}
          visibleDeviceIds={selectedDeviceIds}
          onToggleDevice={handleToggleDevice}
          onAddDevice={(plotTarget) => openAddDeviceModal(plotTarget || currentPlot)}
          isDeviceDetailPage={true}
        />
      </div>

      {/* Requirement 1: Empty state message when no physical devices exist */}
      {physicalDevices.length === 0 ? (
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '40px 20px',
          border: '1px solid #e2e8f0',
          textAlign: 'center',
          color: '#475569',
          marginBottom: '24px'
        }}>
          <span style={{ fontSize: '32px', display: 'block', marginBottom: '12px' }}>📡</span>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#0f172a' }}>No Hardware Devices Provisioned</h3>
          <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#64748b', maxWidth: '480px', marginLeft: 'auto', marginRight: 'auto' }}>
            There are currently no physical telemetry sensors assigned to this plot. Click below to provision your first hardware device.
          </p>
          <button
            type="button"
            onClick={() => openAddDeviceModal(currentPlot)}
            style={{
              background: '#166534',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            + Add Physical Device
          </button>
        </div>
      ) : (
        <>
          {/* Display Controls */}
          <DisplayControls
            categoryGroups={categoryGroups}
            activeMetrics={activeMetrics}
            onToggleMetric={handleToggleMetric}
            localTimeEnabled={localTimeEnabled}
            onToggleLocalTime={setLocalTimeEnabled}
            forecastVisible={forecastVisible}
            onToggleForecast={setForecastVisible}
            forecastDisabled={true}
            disabledSections={disabledSections}
            onToggleSection={(sec) => setDisabledSections(prev => ({ ...prev, [sec]: !prev[sec] }))}
            devices={physicalDevices}
            visibleDeviceIds={selectedDeviceIds}
          />

          {/* Metric Panels */}
          {['deviceState', 'sensorData'].map((catKey) => {
            const catMetrics = (categoryGroups[catKey] || []).filter((m) => activeMetrics.includes(m.type));
            if (catMetrics.length === 0 || disabledSections[catKey]) return null;

            return (
              <MetricCategoryPanel
                key={catKey}
                title={CATEGORY_TITLES[catKey]}
                categoryKey={catKey}
                metrics={catMetrics}
                chartData={chartData}
                visibleDevices={visibleDevices}
                forecastVisible={forecastVisible}
                localTimeEnabled={localTimeEnabled}
              />
            );
          })}
        </>
      )}

      {/* Provisioning Modal */}
      <DeviceProvisioningModal />
    </div>
  );
}
